import {
	getCaseStudyEntityId,
	getCaseStudyUrl,
	ORGANIZATION_ENTITY_ID
} from '@/constants/schema-ids';
import { extractPlainTextFromBlocksContent } from '@/lib/blocks-content';
import { formatSchemaDate } from '@/lib/blog-posting-schema';
import { getCaseStudyBreadcrumbTitle } from '@/lib/breadcrumbs';
import { CaseStudyDto } from '@/types/case-studies';

type CaseStudyPage = CaseStudyDto & {
	publishDate?: string;
	contentUpdatedAt?: string;
	publishedAt?: string;
};

type StatsBlock = {
	__component: 'case.stats';
	items?: { value?: string; text?: unknown }[];
};

const getStats = (content: CaseStudyDto['content']) =>
	(content ?? [])
		.filter((block): block is StatsBlock => (block as StatsBlock)?.__component === 'case.stats')
		.flatMap(block => block.items ?? [])
		.map(item => ({
			'@type': 'QuantitativeValue',
			name: extractPlainTextFromBlocksContent(item.text as never).trim(),
			value: item.value
		}))
		.filter(stat => stat.name && stat.value);

export const buildCaseStudyJsonLd = (page: CaseStudyPage) => {
	if (!page.seo?.url) return null;

	const slug = page.seo.url;
	const image =
		page.seo.preview?.url ?? page.cardPreview?.find(card => card.image?.url)?.image?.url;
	const datePublished = formatSchemaDate(page.publishDate ?? page.publishedAt);
	// Same "meaningful update" chain as blog posts and the sitemap.
	const dateModified = formatSchemaDate(
		page.contentUpdatedAt ?? page.publishDate ?? page.publishedAt
	);
	const keywords = page.tags?.map(tag => tag.name).join(', ');
	const awards = page.awards?.map(award =>
		[award.fullName ?? award.shortName, award.year].filter(Boolean).join(' ')
	);
	const stats = getStats(page.content);

	return {
		'@context': 'https://schema.org',
		'@type': 'CreativeWork',
		'@id': getCaseStudyEntityId(slug),
		name: page.seo.title,
		headline: getCaseStudyBreadcrumbTitle(page as never),
		url: getCaseStudyUrl(slug),
		description: page.seo.description,
		genre: 'Case study',
		inLanguage: 'en',
		creator: { '@id': ORGANIZATION_ENTITY_ID },
		publisher: { '@id': ORGANIZATION_ENTITY_ID },
		...(image ? { image } : {}),
		...(datePublished ? { datePublished } : {}),
		...(dateModified ? { dateModified } : {}),
		...(keywords ? { keywords } : {}),
		...(awards?.length ? { award: awards } : {}),
		...(stats.length ? { mentions: stats } : {})
	};
};
